import React, { useCallback, useEffect, useRef } from "react";
import { SwipeableItem } from "./swipeable-item";
import {
  Pagination,
  PaginationContent,
  PaginationNext,
} from "@bindery/ui/components/ui/pagination";
import { useFluencyTimer } from "@bindery/ui/hooks/use-fluency-timer";
import { useSpaceToAdvance } from "./use-vocabulary-keyboard";
import type { Logger } from "@bindery/logging";
import type {
  VocabularyWord,
  WordStatus,
} from "@/ear-reader/types/vocabulary-exercise";

// Types

export interface WordReadingFluencyProps {
  words: VocabularyWord[];
  onStatusChange: (id: number, status: WordStatus) => void;
  markDisfluency: (index: number | null) => void;
  activeIndex: number | null;
  setActiveIndex: (
    value: number | null | ((prev: number | null) => number | null),
  ) => void;
  /** Whether the aide drawer is open (suppresses keyboard handling) */
  isDrawerOpen: boolean;
  /** Logger instance for structured telemetry (required for child components) */
  log: Logger;
}

/** No-op focus handler for the always-focused active item */
const NOOP = () => {
  /* no-op */
};

// WordReadingFluency

/**
 * Fluency testing for vocabulary words. Presents one word at a time with a
 * 3-second review timer. When the timer expires the word is auto-marked as
 * disfluent. Recognized words auto-advance after a short delay.
 *
 * Timer state is managed by `useFluencyTimer` (reducer-based state machine).
 * Keyboard handling is in `useSpaceToAdvance`.
 *
 * Implementation notes (ref-update pattern):
 * - `timerExpiredRef` and `handleRecognizedRef` store the latest values of
 *   `timerExpired` and `handleRecognized` to keep `handleWordStatusChange`
 *   stable across renders. This prevents unnecessary re-renders of
 *   SwipeableItem while ensuring the callback always uses current values.
 * - The refs are updated in a useEffect whenever their dependencies change.
 * - This pattern trades some indirection for stable callback references,
 *   which is valuable when callbacks are passed to memoized children.
 */
export const WordReadingFluency: React.FC<WordReadingFluencyProps> = ({
  words,
  onStatusChange,
  markDisfluency,
  activeIndex,
  setActiveIndex,
  isDrawerOpen,
  log,
}) => {
  const activeItemRef = useRef<HTMLDivElement | null>(null);

  // Resolve a word slug by index (for logging)
  const getWordSlug = useCallback(
    (index: number) => words[index]?.slug,
    [words],
  );

  // --- Fluency timer (state machine) ---
  const {
    phase,
    activeIndex: timerActiveIndex,
    handleRecognized,
    advanceToNextWord,
    setActiveIndex: setTimerActiveIndex,
  } = useFluencyTimer({
    initialIndex: activeIndex,
    onTimerExpired: markDisfluency,
    log,
    getWordSlug,
  });

  // Derive timer-expired state from the phase (Issue 10 — single source of truth)
  const timerExpired = phase === "expired";

  // Sync timer's activeIndex back to parent store
  useEffect(() => {
    if (timerActiveIndex !== activeIndex) {
      setActiveIndex(timerActiveIndex);
    }
  }, [timerActiveIndex, activeIndex, setActiveIndex]);

  useEffect(() => {
    if (activeIndex !== timerActiveIndex) {
      setTimerActiveIndex(activeIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- parent-driven changes only; reacting to timerActiveIndex would loop with the effect above
  }, [activeIndex]);

  // --- Keyboard: space to advance when timer expired ---
  useSpaceToAdvance({
    enabled: timerExpired && activeIndex !== null && activeIndex < words.length,
    onAdvance: advanceToNextWord,
    isDrawerOpen,
  });

  // --- Status change with auto-advance on recognition ---
  //
  // Why refs instead of direct dependencies?
  // `handleWordStatusChange` is passed to memoized SwipeableItem children.
  // Including `timerExpired` and `handleRecognized` in the dependency array
  // would recreate the callback on every timer tick, causing SwipeableItem
  // to re-render. Refs let us read the latest values without invalidating
  // the callback identity.
  //
  // Safety: the useEffect below synchronizes refs whenever the source values
  // change, so timerExpiredRef.current is always up-to-date before the next
  // user interaction can invoke handleWordStatusChange.
  const timerExpiredRef = useRef(timerExpired);
  const handleRecognizedRef = useRef(handleRecognized);
  useEffect(() => {
    timerExpiredRef.current = timerExpired;
    handleRecognizedRef.current = handleRecognized;
  }, [timerExpired, handleRecognized]);

  const handleWordStatusChange = useCallback(
    (id: number, status: "recognized" | "unrecognized" | "unreviewed") => {
      onStatusChange(id, status);

      // Auto-advance when the word is recognized and the timer hasn't expired.
      // This side effect is intentional: recognition during the active phase
      // triggers advancement to keep fluency testing flowing.
      if (status === "recognized" && !timerExpiredRef.current) {
        handleRecognizedRef.current();
      }
    },
    [onStatusChange],
  );

  // --- Focus the active item when it changes ---
  useEffect(() => {
    activeItemRef.current?.focus();
  }, [activeIndex]);

  // --- Restore focus to the active item when the aide drawer closes ---
  //
  // When the aide drawer opens (e.g., on timer expiry), it steals focus.
  // When it closes, use-word-engagement tries to restore focus to the
  // markdown-word element, but that element is null inside the vocabulary
  // exercise — leaving the document body focused and silently breaking the
  // right-arrow ("recognized") handler. We refocus the active SwipeableItem
  // here so keyboard navigation continues to work after the drawer closes.
  const prevDrawerOpenRef = useRef(isDrawerOpen);
  useEffect(() => {
    const wasOpen = prevDrawerOpenRef.current;
    prevDrawerOpenRef.current = isDrawerOpen;
    if (wasOpen && !isDrawerOpen) {
      activeItemRef.current?.focus();
    }
  }, [isDrawerOpen]);

  // --- Render ---

  return (
    <>
      {activeIndex !== null && activeIndex < words.length && (
        <div className="bg-ear-teal/10 mb-6 rounded-lg p-4">
          <SwipeableItem
            word={words[activeIndex] ?? { id: 0, text: "", slug: "" }}
            onStatusChange={handleWordStatusChange}
            isFocused={true}
            onFocus={NOOP}
            index={activeIndex}
            ref={activeItemRef}
            isDrawerOpen={isDrawerOpen}
            log={log}
          />
          <div className="mt-4">
            {timerExpired && activeIndex < words.length ? (
              <Pagination>
                <PaginationContent>
                  <PaginationNext onClick={advanceToNextWord}>
                    Next
                  </PaginationNext>
                </PaginationContent>
              </Pagination>
            ) : (
              <div className="text-muted-foreground mx-auto animate-pulse text-center">
                building fluency...
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
